import { useState } from "react";
import { toast } from "sonner";
import { TriangleAlert } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/pages/Servers";
import { api, errMsg } from "@/lib/api";

export const WarnDialog = ({ cid, gamertag, onDone, defaultReason = "" }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(defaultReason);
  const [severity, setSeverity] = useState("minor");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/communities/${cid}/warnings`, { gamertag, reason, severity });
      toast.success(`Warning logged for ${gamertag} (${data.active_warnings} active)`);
      if (data.escalation) toast.warning(data.escalation);
      setOpen(false); setReason(defaultReason); setSeverity("minor"); onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`warn-player-${gamertag}`} className="flex items-center gap-1 rounded-sm border border-amber-500/30 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-400 hover:bg-amber-500/10">
          <TriangleAlert className="h-3 w-3" />Warn
        </button>
      </DialogTrigger>
      <DialogContent className="border-[#1E2D4A] bg-[#0B1120]">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wider">Warn {gamertag}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Textarea data-testid="warn-reason-input" required minLength={3} maxLength={300} placeholder="Reason (e.g. raiding during PvE)" value={reason} onChange={(e) => setReason(e.target.value)} className="border-[#1E2D4A] bg-[#050811]" aria-label="Reason" />
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger data-testid="warn-severity-select" className="border-[#1E2D4A] bg-[#050811]"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[#1E2D4A] bg-[#0B1120]"><SelectItem value="minor">Minor</SelectItem><SelectItem value="major">Major</SelectItem></SelectContent>
          </Select>
          <p className="text-xs text-slate-500">Logged to moderation history and the audit log. Linked players also get a Discord DM. Nothing is sent in-game.</p>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setOpen(false)} className={BTN_SECONDARY}>Cancel</button>
            <button data-testid="warn-submit-button" disabled={busy} className={BTN_PRIMARY}>{busy ? "Saving…" : "Log warning"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
